/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * useLinkedinMatch.js
 *
 * Resolves an e-mail address to the CRM lead / customer that owns it AND carries a LinkedIn profile
 * (`POST /api/linkedin/resolve`), so the LinkedIn button can appear next to an e-mail action on screens
 * that only know the address (mail thread, follow-up row, contact card). Lookups are micro-batched —
 * a list of fifty rows issues ONE request — and memoised for the session, misses included.
 *
 * Does nothing (and sends nothing) while the user has no connected LinkedIn account.
 *
 * @module hooks/useLinkedinMatch
 */

import React from 'react';
import linkedinApi from '../services/linkedinApi';
import useLinkedinStatus from './useLinkedinStatus';

/** Batch window. @type {number} */
const BATCH_MS = 40;

/** Backend cap per resolve call. @type {number} */
const MAX_BATCH = 100;

/** @type {Map<string, object|null>} e-mail → match (null = looked up, no LinkedIn profile). */
const memo = new Map();

/** @type {Map<string, Array<(match: object|null) => void>>} */
const waiting = new Map();

let timer = null;

/** Flush the queued e-mails in chunks of {@link MAX_BATCH}. @returns {Promise<void>} */
const flush = async () => {
    timer = null;

    const emails = Array.from(waiting.keys());

    for (let i = 0; i < emails.length; i += MAX_BATCH) {
        const chunk = emails.slice(i, i + MAX_BATCH);
        let matches = [];

        try {
            matches = (await linkedinApi.resolveContacts({emails: chunk}))?.matches || [];
        } catch (_e) {
            matches = [];
        }

        chunk.forEach((email) => {
            const hit = matches.find((match) => String(match.email || '').toLowerCase() === email) || null;

            memo.set(email, hit);
            (waiting.get(email) || []).forEach((resolve) => resolve(hit));
            waiting.delete(email);
        });
    }
};

/**
 * Look one address up (batched + memoised).
 * @param {string} email Normalised (lower-case) address.
 * @returns {Promise<object|null>}
 */
export const lookupLinkedinByEmail = (email) => {
    if (memo.has(email)) {
        return Promise.resolve(memo.get(email));
    }

    return new Promise((resolve) => {
        waiting.set(email, [...(waiting.get(email) || []), resolve]);

        if (!timer) {
            timer = globalThis.setTimeout(flush, BATCH_MS);
        }
    });
};

/** Forget every memoised lookup (after a lead / customer gains a profile URL). @returns {void} */
export const clearLinkedinMatches = () => memo.clear();

/**
 * @param {string} [email] Address to resolve.
 * @param {boolean} [skip=false] Skip when the caller already has the profile URL.
 * @returns {{match: ?{linkedinUrl: string, entityType?: string, entityId?: string, name?: string, company?: string}}}
 */
export default function useLinkedinMatch(email, skip = false) {
    const {ready} = useLinkedinStatus();
    const key = String(email || '').trim().toLowerCase();
    const [match, setMatch] = React.useState(key && memo.has(key) ? memo.get(key) : null);

    React.useEffect(() => {
        if (!ready || skip || !key || !key.includes('@')) {
            setMatch(null);

            return undefined;
        }

        let alive = true;

        lookupLinkedinByEmail(key).then((hit) => alive && setMatch(hit));

        return () => {
            alive = false;
        };
    }, [ready, skip, key]);

    return {match};
}
